// Course Package Import — Step 5 block creator (all 12 types).
// Creates each block via existing endpoints, links the Step-4 uploads, uploads
// reading images + reading PDFs AFTER the RICHTEXT block exists (role-safe),
// sanitises every reading HTML, and stamps batch/course/key on each block.
// A failed block is reported; the rest carry on. PRD §5.3 / §7.

import sanitizeHtml from '../../lib/sanitize';
import { markdownToHtml } from './markdownToHtml';
import { normaliseQuestion, csvToRawQuestions, QUIZ_QUESTION_TYPES, ALL_QUESTION_TYPES } from './questionsCsv';
import { ext, errText, blob, MULTIPART } from './packageUpload';

const td = new TextDecoder();
const textOf = (files, rel) => (files.has(rel) ? td.decode(files.get(rel)) : null);

function stamps(batchId, courseId, topicKey, key, pkg) {
  return { importBatchId: batchId, importCourseId: courseId, importKey: key, importTopicKey: topicKey, importPackage: pkg };
}

// ---- questions ----
function getQuestions(b, files, allowed) {
  const q = b.questions;
  let raw = null;
  if (Array.isArray(q)) raw = q;
  else if (q && q.file) {
    const text = textOf(files, q.file);
    if (text == null) return [];
    if (/\.csv$/i.test(q.file)) { const { raw: r } = csvToRawQuestions(text); raw = r || []; }
    else { try { raw = JSON.parse(text); } catch (e) { raw = []; } }
  } else return [];
  const resolveCode = (ref) => textOf(files, ref) ?? undefined;
  const out = [];
  (raw || []).forEach((rq, i) => {
    const { question, error } = normaliseQuestion(rq, { allowedTypes: allowed, resolveCode });
    if (!error) out.push({ id: `q${i + 1}`, ...question });
  });
  return out;
}

// ---- video url parsing (PRD §5.3) ----
function videoContent(url) {
  const c = {};
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
  const vimeo = url.match(/vimeo\.com\/(\d+)(?:\/(\w+))?/i);
  if (yt) { c.youtubeUrl = url; c.videoProvider = 'youtube'; }
  else if (vimeo) { c.vimeoId = vimeo[1]; c.videoProvider = 'vimeo'; if (vimeo[2]) c.vimeoPrivateHash = vimeo[2]; }
  else if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) { c.videoUrl = url; }
  return c;
}

// Build the create payload for a non-reading, non-AILab block.
function buildPayload(b, assets, files, st) {
  const base = { type: null, title: b.title, content: { ...(b.content || {}), ...st }, settings: { ...(b.settings || {}) }, isMandatory: b.isMandatory !== false };
  const a = assets.blocks[b.key] || {};
  switch (b.type) {
    case 'Video':
      base.type = 'Video';
      Object.assign(base.content, videoContent((b.video && b.video.url) || ''));
      return base;
    case 'Slides':
    case 'LiveClassSlides': {
      base.type = b.type === 'Slides' ? 'PPT' : 'LiveClassPPT';
      if (a.deck) { base.fileUrl = a.deck.url; base.fileName = a.deck.name; }
      if (b.type === 'LiveClassSlides') {
        const tg = b.trainerGuide;
        if (tg) base.content.trainerGuide = typeof tg === 'string' ? tg : (tg.file ? textOf(files, tg.file) : '');
        if (Array.isArray(b.speakerNotes)) base.content.speakerNotes = b.speakerNotes;
      }
      return base;
    }
    case 'LiveSession':
      base.type = 'LiveSession';
      return base;
    case 'Quiz': {
      base.type = 'Quiz';
      const qs = getQuestions(b, files, QUIZ_QUESTION_TYPES);
      base.content.questions = qs;
      if (base.content.totalPoints == null) base.content.totalPoints = qs.reduce((s, q) => s + (q.points || 0), 0);
      return base;
    }
    case 'Assignment': {
      base.type = 'Assignment';
      const qs = getQuestions(b, files, ALL_QUESTION_TYPES);
      base.content.questions = qs;
      if (base.content.totalPoints == null) base.content.totalPoints = qs.reduce((s, q) => s + (q.points || 0), 0);
      if (Array.isArray(b.attachments) && b.attachments.length) {
        const lines = (b.attachments || []).filter((p) => assets.blocks[b.key]?.attachments?.[p])
          .map((p) => `Attachment: ${p.split('/').pop()} — ${assets.blocks[b.key].attachments[p]}`);
        if (lines.length) base.content.instructions = `${base.content.instructions || ''}\n\n${lines.join('\n')}`.trim();
      }
      if (b.fields && (b.fields.grading || b.fields.gradingMode)) {
        base.gradingMode = b.fields.gradingMode; base.grading = b.fields.grading;
      }
      return base;
    }
    case 'GuidedLab': {
      base.type = 'Lab';
      const ds = a.datasets || [];
      base.content.datasets = ds.map((d) => d.name);
      base.content.importDatasets = ds.map((d) => ({ datasetId: d.datasetId, name: d.name, fileName: d.file }));
      return base;
    }
    case 'LeanCanvas':
      base.type = 'LeanCanvas';
      return base;
    case 'StakeholderSim':
      base.type = 'StakeholderSim';
      return base;
    case 'IndustryLab':
      base.type = 'IndustryLab';
      return base;
    default:
      return null;
  }
}

async function createOne(ctx, topicId, topicKey, b) {
  const { api, orgId, courseId, readResult, assets, batchId, pkg } = ctx;
  const files = readResult.files;
  const st = stamps(batchId, courseId, topicKey, b.key, pkg);

  // ---- Reading: create shell, upload pdf/images (block-scoped), sanitise, PUT
  if (b.type === 'Reading') {
    const create = await api.post(`/organizations/${orgId}/topics/${topicId}/content-blocks`, {
      type: 'RICHTEXT', title: b.title, content: { description: '', placeholder: false, ...(b.content || {}) }, settings: b.settings || {},
    });
    const blockId = create.data.id;
    const src = b.source || {};
    let html = null; let pdfMeta = null;
    if (src.pdf && files.has(src.pdf)) {
      const fd = new FormData(); fd.append('file', blob(files.get(src.pdf), 'application/pdf'), src.pdf.split('/').pop()); fd.append('block_id', blockId);
      const r = await api.post('/content-blocks/upload-pdf', fd, MULTIPART);
      html = r.data.html || ''; pdfMeta = { pdfFileId: r.data.pdfFileId, pdfFileName: r.data.pdfFileName, wordCount: r.data.wordCount };
    } else if (src.file && files.has(src.file)) {
      const text = textOf(files, src.file); html = /\.html?$/i.test(src.file) ? text : markdownToHtml(text);
    } else if (typeof src.html === 'string') html = src.html;
    else if (typeof src.markdown === 'string') html = markdownToHtml(src.markdown);
    html = html || '';

    // images (block-scoped)
    const refs = new Set(); const re = /(?:src|href)=["']([^"']+)["']/g; let m;
    while ((m = re.exec(html))) { const p = m[1].replace(/^\.\//, ''); if (files.has(p) && /^(png|jpe?g|gif|webp|svg)$/.test(ext(p))) refs.add(p); }
    for (const p of refs) {
      try {
        const fd = new FormData(); fd.append('file', blob(files.get(p)), p.split('/').pop()); fd.append('block_id', blockId);
        const r = await api.post('/content-blocks/upload-image', fd, MULTIPART);
        html = html.split(`"${p}"`).join(`"${r.data.url}"`).split(`'${p}'`).join(`'${r.data.url}'`).split(`"./${p}"`).join(`"${r.data.url}"`);
      } catch (e) { /* image best-effort; reading still imports */ }
    }
    const clean = sanitizeHtml(html);
    await api.put(`/organizations/${orgId}/content-blocks/${blockId}`, {
      content: { ...(b.content || {}), description: clean, html: clean, wordCount: pdfMeta?.wordCount, pdfFileId: pdfMeta?.pdfFileId, pdfFileName: pdfMeta?.pdfFileName, ...st },
    });
    return { blockId, note: `reading ready (${clean.length} chars, sanitised)` };
  }

  // ---- AILab: lab endpoint creates lab + block; link + experience
  if (b.type === 'AILab') {
    const a = assets.blocks[b.key] || {};
    const lab = b.lab || {};
    let workflow = b.workflow && b.workflow.file ? JSON.parse(textOf(ctx.readResult.files, b.workflow.file) || '{}') : (b.workflow || {});
    // replace dataset file refs in workflow nodes
    const dsById = {}; (a.datasets || []).forEach((d) => { dsById[d.file] = d; });
    if (workflow && Array.isArray(workflow.nodes)) {
      workflow.nodes.forEach((n) => {
        const f = n?.data?.datasetFile; if (f && dsById[f]) { n.data.datasetId = n.data.recipeId = n.data.modelId = dsById[f].datasetId; n.data.isBuiltin = false; n.data.label = dsById[f].name; delete n.data.datasetFile; }
      });
    }
    const created = await api.post(`/orgs/${orgId}/labs`, { title: b.title, mode: 'Standard', courseId, topicId, workflow, ...lab });
    const labId = created.data.id || created.data.labId;
    // find the auto-created block
    const list = await api.get(`/organizations/${orgId}/topics/${topicId}/content-blocks`);
    const blk = (list.data || []).find((x) => x.type === 'Lab' && x.content && x.content.labId === labId);
    const blockId = blk ? blk.id : null;
    if (blockId) {
      await api.put(`/organizations/${orgId}/content-blocks/${blockId}`, {
        content: { ...(blk.content || {}), ...(b.content || {}), importDatasets: (a.datasets || []).map((d) => ({ datasetId: d.datasetId, name: d.name })), ...st },
        settings: b.settings || {}, isMandatory: b.isMandatory !== false,
      });
    }
    if (b.experience) {
      try {
        const exp = b.experience.file ? JSON.parse(textOf(ctx.readResult.files, b.experience.file) || '{}') : b.experience;
        await api.put(`/orgs/${orgId}/labs/${labId}/experience/drafts/1`, exp);
        await api.post(`/orgs/${orgId}/labs/${labId}/experience/drafts/1/publish`, {});
      } catch (e) { return { blockId, note: `lab created; experience not published: ${errText(e)}` }; }
    }
    return { blockId, note: 'AI lab created' };
  }

  // ---- all other types ----
  const payload = buildPayload(b, assets, files, st);
  if (!payload) throw new Error(`unsupported type ${b.type}`);
  const create = await api.post(`/organizations/${orgId}/topics/${topicId}/content-blocks`, payload);
  return { blockId: create.data.id, note: 'created' };
}

/**
 * Create all blocks across topics. ctx: api, orgId, courseId, readResult, plan,
 * assets, batchId, pkg, topicIdByKey(Map), existingImportKeys(Set), onProgress.
 */
export async function createBlocks(ctx) {
  const index = ctx.readResult.index || {};
  const onProgress = ctx.onProgress || (() => {});
  const onItem = ctx.onItem || (() => {});
  const items = [];
  const push = (it) => { items.push(it); try { onItem({ group: 'block', ...it }); } catch (e) { /* ignore */ } };
  const topics = Array.isArray(index.topics) ? index.topics : [];
  let i = 0; let total = 0;
  topics.forEach((t) => (total += (t.blocks || []).length));
  for (const t of topics) {
    const topicId = ctx.topicIdByKey.get(t.key);
    const planTopic = (ctx.plan?.topics || []).find((p) => p.key === t.key);
    for (const b of (t.blocks || [])) {
      i += 1;
      onProgress({ phase: 'blocks', current: i, total, name: b.title });
      const planBlock = (planTopic?.blocks || []).find((p) => p.key === b.key);
      if (!topicId) { push({ key: b.key, type: b.type, title: b.title, status: 'not-created', message: 'topic not created' }); continue; }
      if (planBlock && planBlock.status === 'error') { push({ key: b.key, type: b.type, title: b.title, status: 'skipped', message: 'fix errors in preview' }); continue; }
      if (ctx.existingImportKeys && ctx.existingImportKeys.has(b.key)) { push({ key: b.key, type: b.type, title: b.title, status: 'skipped', message: 'already imported' }); continue; }
      try {
        const res = await createOne(ctx, topicId, t.key, b);
        push({ key: b.key, type: b.type, title: b.title, status: 'created', blockId: res.blockId, message: res.note });
      } catch (e) {
        const msg = b.type === 'IndustryLab' ? "This Industry Lab isn't in the catalog." : errText(e);
        push({ key: b.key, type: b.type, title: b.title, status: 'error', message: msg });
      }
    }
  }
  return items;
}
